import { expectedHint, type ExampleEntry } from '../examples/loadExamples';
import { useI18n } from '../i18n';
import { GarmentIcon, garmentFor } from '../ui/Icon';
import { StitchRule } from '../ui/Knit';
import { IntakeBlock, type IntakeActions } from './Intake';

/**
 * Shown while the editor is empty (first visit, or after "Effacer"): what it does, how to start with
 * her own pattern (paste, open a file, a starter), privacy, and the example patterns.
 */
export function WelcomePanel({
  examples,
  onOpen,
  intake,
  intakeReady = true,
}: {
  examples: ExampleEntry[] | null;
  onOpen: (id: string) => void;
  intake: IntakeActions;
  /** False for the few moments the saved draft (or a shared link) takes to come back. */
  intakeReady?: boolean;
}) {
  const { t, lang } = useI18n();
  const notes = [t('welcome.line1'), t('welcome.line2'), t('welcome.line3')];
  return (
    <div className="relative min-h-0 flex-1 overflow-y-auto" data-testid="welcome">
      <div className="mx-auto max-w-measure px-6 pb-24 pt-10 sm:px-10">
        <p className="kicker">{t('welcome.kicker')}</p>
        <h2 className="font-display mt-3 text-3xl font-[400] leading-tight text-fg">{t('welcome.title')}</h2>
        <p className="font-display mt-4 text-lg font-[360] leading-relaxed text-fg">{t('welcome.lede')}</p>
        <IntakeBlock actions={intake} ready={intakeReady} />
        <ol className="mt-8 space-y-3">
          {notes.map((text, i) => (
            <li key={text} className="flex items-baseline gap-4 text-sm leading-relaxed text-muted">
              <span className="font-display w-5 shrink-0 text-md text-subtle tabular">{i + 1}</span>
              <span>{text}</span>
            </li>
          ))}
        </ol>

        {examples && examples.length > 0 && (
          <>
            <StitchRule className="my-10" />
            <section aria-labelledby="welcome-examples">
              <h3 id="welcome-examples" className="font-display text-xl font-[440] text-fg">
                {t('welcome.examples')}
              </h3>
              <p className="mt-2 text-sm text-muted">{t('welcome.examplesHint')}</p>
              <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {examples.map((ex) => {
                  const hint = expectedHint(ex, t);
                  const description = lang === 'fr' ? ex.description_fr : ex.description_en;
                  return (
                    <li key={ex.id}>
                      <button
                        type="button"
                        onClick={() => onOpen(ex.id)}
                        aria-label={t('welcome.open', { title: ex.title })}
                        data-testid="example-card"
                        data-example={ex.id}
                        className="group flex h-full w-full flex-col items-start gap-3 rounded-card bg-surface p-5 text-left transition-colors hover:bg-editor hover:ring-1 hover:ring-inset hover:ring-line-strong"
                      >
                        <span className="flex w-full items-start gap-3">
                          <GarmentIcon name={garmentFor(`${ex.id} ${ex.title}`)} size={24} className="mt-0.5 text-accent-strong" />
                          <span className="min-w-0 flex-1">
                            <span className="font-display block text-md font-[500] leading-snug text-fg" data-verbatim lang={ex.lang}>
                              {ex.title}
                            </span>
                            <span className="kicker mt-1 block tracking-[0.08em]">
                              {ex.lang.toUpperCase()}
                              {ex.sizes.length ? ` · ${ex.sizes.join(', ')}` : ''}
                            </span>
                          </span>
                        </span>
                        {description && (
                          <span className="line-clamp-4 text-xs leading-relaxed text-muted" title={description}>
                            {description}
                          </span>
                        )}
                        {hint && <span className="mt-auto rounded-pill bg-heather/[0.12] px-2 py-0.5 text-2xs font-bold text-fg">{hint}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
